import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';
import {PHONE_TOUCH, phoneSheetItemSx, phoneSheetSx} from 'src/components/mobile/mobileDesign.sx';

/**
 * The sheet itself: the shared phone dialog, anchored to the bottom edge.
 *
 * MUI centres a dialog's container; a sheet rises from the bottom, which is the only thing added
 * here — every other value is `phoneSheetSx`, so a sheet of actions, a sheet of filters and a sheet
 * of export options cannot drift apart.
 */
export const sheetSx: SxProps<Theme> = {
    ...(phoneSheetSx as object),
    '& .MuiDialog-container': {
        alignItems: 'flex-end',
    },
};

/** Badge, name, the line under it — the record, named before anything is done to it. */
export const headSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 16px 12px',
    borderBottom: `1px solid ${theme.palette.neutral[100]}`,
};

export const identitySx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
};

/**
 * The actions themselves.
 *
 * The children are the very nodes the popover was handed, so the shape of an action is styled here
 * rather than rewritten at each call site: a labelled action becomes a 56px line with its icon at
 * the left, and an icon-only one — the desktop row's 28px square — grows to a finger's target.
 */
export const actionsSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    '& .MuiButton-root': {
        ...(phoneSheetItemSx() as object),
        minWidth: 0,
        '&.Mui-disabled': {
            opacity: 0.45,
        },
    },
    '& .MuiButton-root .MuiTypography-root': {
        width: '100%',
        textAlign: 'start',
        fontSize: '15px',
        fontWeight: 500,
        color: 'inherit',
    },
    // Doubled, because the components these come from set their own 28px width and their own right
    // margin on the very same element.
    '&& .MuiIconButton-root': {
        width: PHONE_TOUCH,
        height: PHONE_TOUCH,
        marginRight: 0,
        padding: 0,
        borderRadius: '10px',
        color: theme.palette.neutral[600],
    },
    '&& .MuiIconButton-root .MuiSvgIcon-root': {
        width: '21px',
        height: '21px',
        fill: theme.palette.neutral[600],
    },
};

/**
 * A line holding actions that have icons and no labels.
 *
 * Some menus are made of icon buttons a desktop row lays out side by side. There is no label to put
 * on a line of its own and none may be invented here, so they keep their row and only grow to the
 * size of a fingertip, on a line as tall as every other.
 */
export const iconRowSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
    minHeight: 56,
    padding: '6px 12px',
    '& .MuiStack-root': {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '4px',
    },
};

/**
 * Whatever destroys something, below a rule.
 *
 * The rule only appears when there is something above it to be separated from: on a menu whose one
 * action is Delete it would be a second line directly under the header's.
 */
export const destructiveSx: SxProps<Theme> = {
    '&:not(:first-child)': {
        borderTop: `1px solid ${theme.palette.neutral[100]}`,
    },
    '&& .MuiButton-root, && .MuiIconButton-root': {
        color: theme.palette.brand.dark,
    },
    '&& .MuiButton-root .MuiTypography-root': {
        color: theme.palette.brand.dark,
    },
    '&&& .MuiSvgIcon-root': {
        color: theme.palette.brand.dark,
        fill: theme.palette.brand.dark,
    },
};

/** Carries the sheet's bottom inset, so the phone's home indicator never sits on the button. */
export const cancelWrapSx: SxProps<Theme> = {
    padding: '8px 16px 22px',
};

export const cancelSx: SxProps<Theme> = {
    width: '100%',
    height: 48,
    borderRadius: '12px',
    textTransform: 'none',
    fontSize: '15px',
    fontWeight: 600,
    border: `1px solid ${theme.palette.neutral[200]}`,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    '&:hover': {
        border: `1px solid ${theme.palette.neutral[300]}`,
        backgroundColor: theme.palette.common.white,
    },
};
